import React, { useMemo } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Typography,
  Autocomplete,
  TextField,
  useTheme,
} from "@mui/material";
import PositionsCalculationControlsV2 from "./PositionsCalculationControlsV2";
import { v2CardBg, v2CardBorder, v2LabelSx, v2MutedSx } from "../v2Theme";

const SORT_OPTIONS = [
  { value: "fundName", label: "Fund" },
  { value: "strategyName", label: "Strategy" },
  { value: "aum", label: "AUM" },
  { value: "longCapital", label: "Long Cap" },
  { value: "shortCapital", label: "Short Cap" },
  { value: "netCapital", label: "Net Cap" },
  { value: "plPer", label: "P(L)% Today" },
  { value: "pL", label: "P(L) Today" },
  { value: "plLong", label: "Long" },
  { value: "plShort", label: "Short" },
  { value: "longPerformance", label: "Long Perf" },
  { value: "shortPerformance", label: "Short Perf" },
  { value: "sizeLong", label: "DA Long" },
  { value: "sizeShort", label: "DA Short" },
  { value: "netAumPercent", label: "Net" },
  { value: "pLYest", label: "Yest" },
  { value: "pLMonth", label: "MTD" },
  { value: "pLQuarter", label: "QTD" },
  { value: "pLYear", label: "YTD" },
  { value: "pLITD", label: "ITD" },
];

const ControlBar = ({
  hideZeroAum,
  onHideZeroAumChange,
  orderBy,
  onOrderByChange,
  order,
  onOrderChange,
  strategyOptions = [],
  strategyQuery = "",
  onStrategyQueryChange,
  fundSummaries = [],
  selectedFundLabel,
  onFundSelect,
}) => {
  const theme = useTheme();

  const ALL_FUNDS_OPTION = useMemo(
    () => ({ abbreviation: null, name: "All funds" }),
    []
  );

  const fundOptions = useMemo(
    () => [ALL_FUNDS_OPTION, ...fundSummaries],
    [ALL_FUNDS_OPTION, fundSummaries]
  );

  const selectedFund = useMemo(() => {
    if (!selectedFundLabel) return ALL_FUNDS_OPTION;
    return (
      fundSummaries.find((f) => f.abbreviation === selectedFundLabel) ||
      ALL_FUNDS_OPTION
    );
  }, [fundSummaries, selectedFundLabel, ALL_FUNDS_OPTION]);

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        flexWrap: "wrap",
        py: 1.25,
        px: 1.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: v2CardBorder(theme),
        backgroundColor: v2CardBg(theme),
        color: "text.primary",
      }}
    >
      <Autocomplete
        size="small"
        options={fundOptions}
        value={selectedFund}
        onChange={(_event, next) => {
          onFundSelect?.(next?.abbreviation ?? null);
        }}
        getOptionLabel={(option) =>
          option?.name || option?.abbreviation || "All funds"
        }
        isOptionEqualToValue={(option, value) =>
          option?.abbreviation === value?.abbreviation
        }
        filterOptions={(options, { inputValue }) => {
          const q = inputValue.trim().toLowerCase();
          if (!q) return options;
          return options.filter(
            (f) =>
              f.abbreviation == null ||
              f.abbreviation?.toLowerCase().includes(q) ||
              f.name?.toLowerCase().includes(q)
          );
        }}
        disableClearable
        blurOnSelect
        sx={{ width: { xs: "100%", sm: 260 }, minWidth: 220 }}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          const isAll = option.abbreviation == null;
          return (
            <Box
              component="li"
              key={key}
              {...optionProps}
              sx={{ display: "block !important", minWidth: 0 }}
            >
              <Typography
                sx={{
                  fontWeight: isAll ? 800 : 700,
                  fontSize: "0.9rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {option.name || option.abbreviation}
              </Typography>
              {!isAll &&
                option.name &&
                option.abbreviation &&
                option.name !== option.abbreviation && (
                  <Typography
                    sx={{
                      fontSize: "0.72rem",
                      fontWeight: 600,
                      ...v2MutedSx(theme),
                    }}
                  >
                    {option.abbreviation}
                  </Typography>
                )}
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Fund"
            placeholder="All funds"
            variant="outlined"
            size="small"
          />
        )}
        slotProps={{
          popper: {
            sx: {
              zoom: 0.75,
              zIndex: (t) => t.zIndex.modal + 1,
            },
          },
          paper: {
            sx: {
              borderRadius: 1.5,
              border: `1px solid ${v2CardBorder(theme)}`,
              backgroundColor: v2CardBg(theme),
              backgroundImage: "none",
            },
          },
        }}
      />

      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel id="v2-sort-by-label" sx={{ color: "text.secondary" }}>
          Sort by
        </InputLabel>
        <Select
          labelId="v2-sort-by-label"
          value={orderBy}
          label="Sort by"
          onChange={(e) => onOrderByChange(e.target.value)}
          sx={{ color: "text.primary" }}
        >
          {SORT_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 100 }}>
        <InputLabel id="v2-sort-dir-label" sx={{ color: "text.secondary" }}>
          Order
        </InputLabel>
        <Select
          labelId="v2-sort-dir-label"
          value={order}
          label="Order"
          onChange={(e) => onOrderChange(e.target.value)}
          sx={{ color: "text.primary" }}
        >
          <MenuItem value="desc">Desc</MenuItem>
          <MenuItem value="asc">Asc</MenuItem>
        </Select>
      </FormControl>

      <PositionsCalculationControlsV2 />

      <Autocomplete
        size="small"
        freeSolo
        options={strategyOptions}
        inputValue={strategyQuery}
        onInputChange={(_event, value) => onStrategyQueryChange?.(value)}
        onChange={(_event, value) => {
          onStrategyQueryChange?.(typeof value === "string" ? value : value || "");
        }}
        sx={{ width: { xs: "100%", sm: 240 }, minWidth: 200 }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search strategy"
            placeholder="Type to filter…"
            variant="outlined"
            size="small"
          />
        )}
      />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          ml: { xs: 0, md: "auto" },
        }}
      >
        <Typography
          variant="body2"
          sx={{ mr: 0.5, fontSize: "0.95rem", ...v2LabelSx(theme) }}
        >
          Hide zero AUM
        </Typography>
        <Switch
          size="small"
          checked={hideZeroAum}
          onChange={onHideZeroAumChange}
          inputProps={{ "aria-label": "Hide strategies with zero AUM" }}
        />
      </Box>
    </Box>
  );
};

export default ControlBar;
